import { asArray } from 'cleaners'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Text, View } from 'react-native'

import {
  asSeasonGame,
  asSeasonSummary,
  asTeamSummary,
  asUserMatch,
  type LiveNow,
  type SeasonGame,
  type SeasonSummary,
  type TeamStationClient,
  type TeamSummary,
  type UserMatch
} from '../../common/api'
import type { GroupKind, GroupRef } from '../../common/groupCreation'
import { localityLabel } from '../../common/locality'
import { asPublicGroup, type PublicGroup } from '../../common/publicGroups'
import { seasonPeriodLabel } from '../../common/seasonTimeFrames'
import { creationIcons } from '../assets'
import {
  Button,
  Card,
  GameHeroCard,
  initialsOf,
  ListCard,
  ListRow,
  LivePill,
  Screen,
  SectionHeader,
  VideoPoster
} from '../components'
import { CreationCard } from '../components/CreationCard'
import { useSceneShown } from '../components/sceneShown'
import { useForegroundRefresh } from '../hooks/useForegroundRefresh'
import { useLatestRequest } from '../hooks/useLatestRequest'
import { useScreenScope } from '../hooks/useScreenScope'
import {
  HOME_FOLLOWS,
  HOME_GROUPS,
  HOME_GUARDED,
  HOME_RECENT,
  HOME_SEASONS,
  HOME_TEAMS
} from '../sync/documentKeys'
import { OFFLINE_UNPLAYABLE, usePhoneOffline } from '../sync/useOffline'
import { cacheStyles, type Theme, useTheme } from '../theme/ThemeContext'
import { UmpireMaskDrawing } from '../umpires/UmpireMaskDrawing'
import { gameStatusLine, matchupLines, shortDate, winnerOf } from './format'
import type { GameListOpenOptions } from './gameListOptions'
import { recentGameIsCurrent } from './recentGameWindow'

export interface HomeScreenProps {
  client: TeamStationClient
  onOpenTeam: (team: TeamSummary) => void
  onOpenSeason: (season: SeasonSummary) => void
  onOpenGame: (
    gameId: string,
    title: string,
    options?: GameListOpenOptions
  ) => void
  onCreateGroup?: (kind: GroupKind) => void
  onOpenGroup?: (ref: GroupRef) => void
  /**
   * A player the person is a guardian of, opened with no team: their whole
   * career rather than one team's part of it.
   */
  onOpenPlayer?: (userId: string, title: string) => void
  /** Home's lists loaded: the phone reads ahead what it needs offline. */
  onLoaded?: () => void
  /** The clock the Recent game's three days are measured on. */
  now?: () => number
  /**
   * Opens the umpire schedule. Given when the person umpires for a league,
   * and Home then offers it as Help Umpire.
   */
  onOpenUmpireSchedule?: () => void
}

/**
 * The first screen: the last game played, the players you are a guardian of,
 * the teams you belong to, and the seasons you may watch.
 *
 * Teams above seasons, on Paul's instruction. Being *on* a team is a stronger
 * thing than being allowed to watch a season, and the person opening this app
 * is usually looking for one child's team — so the teams are the first thing
 * under the thumb, and the seasons are there for the wider wander. That
 * child, for a guardian, is "Your Players", just above the teams.
 */
export function HomeScreen(props: HomeScreenProps): React.ReactElement {
  const { client, onOpenTeam, onOpenSeason, onOpenGame } = props
  const now = props.now ?? Date.now
  /** The newest played game, when it started less than three days ago. */
  const inWindow = useCallback(
    (game: SeasonGame | null | undefined): SeasonGame | undefined =>
      game != null && recentGameIsCurrent(game.startAt, now())
        ? game
        : undefined,
    [now]
  )
  const theme = useTheme()
  const styles = getStyles(theme)
  // Offline, the phone has no stream to play: what was live is not offered.
  const offline = usePhoneOffline(client)

  const [players, setPlayers] = useState<UserMatch[]>([])
  const [teams, setTeams] = useState<TeamSummary[]>([])
  const [groups, setGroups] = useState<PublicGroup[]>([])
  const [followed, setFollowed] = useState<PublicGroup[]>([])
  const [seasons, setSeasons] = useState<SeasonSummary[]>([])
  const [recent, setRecent] = useState<SeasonGame | undefined>(undefined)
  const [live, setLive] = useState<LiveNow[]>([])
  const [liveUrl, setLiveUrl] = useState<string | undefined>(undefined)
  /** Which game `liveUrl` belongs to, so it is not asked for twice. */
  const playing = useRef<string | undefined>(undefined)
  const [recentUrl, setRecentUrl] = useState<string | undefined>(undefined)
  /** `recentUrl` is a stream that is live now. */
  const [recentLive, setRecentLive] = useState(false)
  const recentPlaying = useRef<
    | {
        client: TeamStationClient
        gameId: string
        videoId: string
      }
    | undefined
  >(undefined)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | undefined>(undefined)
  const [recentError, setRecentError] = useState<string | undefined>(undefined)
  const beginRecent = useLatestRequest()
  const refreshRecent = useCallback(async (): Promise<void> => {
    const current = beginRecent()
    try {
      const newest = await client.recentGame()
      if (current()) {
        setRecent(inWindow(newest))
        setRecentError(undefined)
      }
    } catch (caught) {
      if (current())
        setRecentError(caught instanceof Error ? caught.message : 'Cannot load')
    }
  }, [client, beginRecent, inWindow])
  /** The recent game as the phone last stored it, until the read returns. */
  const paintRecent = useCallback(async (): Promise<void> => {
    const latest = beginRecent()
    const stored = await client.cached(HOME_RECENT, asArray(asSeasonGame))
    if (latest() && stored != null) setRecent(inWindow(stored[0]))
  }, [client, beginRecent, inWindow])
  // Each of Home's three loads waits for the home scope's acknowledgement,
  // and reads again when that scope moves past what it read.
  useForegroundRefresh(useScreenScope(refreshRecent, client, paintRecent))

  /**
   * The recent game's own video, so the card moves rather than sitting there.
   *
   * Two extra requests, and both are cheap: the game to find a playable
   * recording, then its signed URL. It is deliberately *after* the screen has
   * drawn — the poster is already showing, so this only ever improves what is
   * on screen and never delays it.
   */
  useEffect(() => {
    const clear = (): void => {
      recentPlaying.current = undefined
      setRecentUrl(undefined)
    }
    if (
      recentPlaying.current?.client !== client ||
      recentPlaying.current?.gameId !== recent?.id
    )
      clear()
    if (recent == null || recent.videoCount < 1) {
      clear()
      return
    }
    let cancelled = false
    const find = async (): Promise<void> => {
      const detail = await client.game(recent.id)
      const playable = detail.videos.find(
        item => item.status === 'ready' || item.status === 'live'
      )
      if (cancelled) return
      if (playable == null) {
        clear()
        return
      }
      // Refresh LIVE metadata without replacing the same video's signed URL.
      // Whether it still streams is read afresh: a game that has ended lets
      // its card go as a live viewer.
      if (recentPlaying.current?.videoId === playable.id) {
        setRecentLive(playable.status === 'live')
        return
      }
      clear()
      const playback = await client.playback(playable.id)
      if (cancelled || playback.url == null) return
      recentPlaying.current = {
        client,
        gameId: recent.id,
        videoId: playable.id
      }
      setRecentLive(playable.status === 'live')
      setRecentUrl(playback.url)
    }
    // A card that does not move is the whole of the failure, and the poster
    // already says what the game was.
    find().catch(() => undefined)
    return () => {
      cancelled = true
    }
  }, [client, recent])

  /**
   * What is on right now, asked for every time this screen opens.
   *
   * Nothing is live on almost every day of the year, and the answer then is an
   * empty list and an empty block. It is asked first all the same: a game that
   * is happening is the only thing on this screen that will not still be true
   * in an hour.
   */
  const beginLook = useLatestRequest()
  const look = useCallback(async (): Promise<void> => {
    const current = beginLook()
    try {
      const now = await client.liveNow()
      if (!current()) return
      setLive(now)
      const first = now[0]
      if (first == null) {
        playing.current = undefined
        setLiveUrl(undefined)
        return
      }
      // The URL carries a token minted for this request, so asking again for
      // the game already playing would hand the player a different address for
      // the same stream — and it would tear the stream down and start it
      // again, every half minute, in front of somebody watching.
      if (playing.current === first.videoId) return
      const playback = await client.playback(first.videoId)
      if (!current() || playback.url == null) return
      playing.current = first.videoId
      setLiveUrl(playback.url)
    } catch {
      // Nothing is live, as far as this screen can tell.
    }
  }, [client, beginLook])
  const lookLive = useScreenScope(look, client)
  // Covered by a screen pushed over it (the live game its hero opened), Home
  // neither plays its preview nor keeps looking: the game would otherwise be
  // pulled twice. Its first look still runs, and Back picks both up again.
  const shown = useSceneShown()
  const looked = useRef(false)
  useEffect(() => {
    if (!shown && looked.current) return
    looked.current = true
    lookLive().catch(() => undefined)
    if (!shown) return
    // A game starts while somebody has the page open — which is the whole
    // point of a live block — so it is looked at again on a slow timer rather
    // than only when the screen is opened.
    const timer = setInterval(() => {
      lookLive().catch(() => undefined)
    }, 30_000)
    return () => {
      clearInterval(timer)
    }
  }, [lookLive, shown])

  // Whether Home can open a player or a group at all, not which function
  // opens one: the shell hands over a new one whenever it renders, and that
  // is no reason to read everything again.
  const opensPlayers = props.onOpenPlayer != null
  const opensGroups = props.onOpenGroup != null
  const loaded = useRef(props.onLoaded)
  loaded.current = props.onLoaded
  const beginLists = useLatestRequest()
  const loadLists = useCallback(async (): Promise<void> => {
    const current = beginLists()
    try {
      const [loadedTeams, loadedSeasons, ownGroups, follows, guarded] =
        await Promise.all([
          client.teams(),
          client.seasons(),
          opensGroups ? client.ownGroups() : Promise.resolve([]),
          // Following is a nicety: Home still opens when it cannot be read.
          opensGroups ? client.follows().catch(() => []) : Promise.resolve([]),
          // So is the list of the players one guards: Home opens without it.
          opensPlayers
            ? client.guardedPlayers().catch(() => [])
            : Promise.resolve([])
        ])
      if (!current()) return
      setPlayers(guarded)
      setTeams(loadedTeams)
      setSeasons(loadedSeasons)
      setGroups(ownGroups)
      setFollowed(follows)
      setError(undefined)
      loaded.current?.()
    } catch (caught: unknown) {
      if (current())
        setError(caught instanceof Error ? caught.message : 'Cannot load')
    } finally {
      if (current()) setLoading(false)
    }
  }, [client, opensGroups, opensPlayers, beginLists])
  /**
   * Home's lists as the phone last stored them, painted before the scope is
   * acknowledged. The web stores none, and stays on its loading state.
   */
  const paintLists = useCallback(async (): Promise<void> => {
    const current = beginLists()
    const [storedTeams, storedSeasons, ownGroups, follows, guarded] =
      await Promise.all([
        client.cached(HOME_TEAMS, asArray(asTeamSummary)),
        client.cached(HOME_SEASONS, asArray(asSeasonSummary)),
        opensGroups
          ? client.cached(HOME_GROUPS, asArray(asPublicGroup))
          : undefined,
        opensGroups
          ? client.cached(HOME_FOLLOWS, asArray(asPublicGroup))
          : undefined,
        opensPlayers
          ? client.cached(HOME_GUARDED, asArray(asUserMatch))
          : undefined
      ])
    if (!current() || storedTeams == null || storedSeasons == null) return
    setTeams(storedTeams)
    setSeasons(storedSeasons)
    setGroups(ownGroups ?? [])
    setFollowed(follows ?? [])
    setPlayers(guarded ?? [])
    setLoading(false)
  }, [client, opensGroups, opensPlayers, beginLists])
  // A play names its game alone: the lists show no game, so they let it
  // pass, and the recent game and what is live take it. A write that moves
  // what the lists count (a game going final, a video, a new game) names
  // the game's teams and season too, and those the lists read.
  const refreshLists = useScreenScope(loadLists, client, paintLists, {
    passGameNotices: true
  })
  useEffect(() => {
    refreshLists().catch(() => undefined)
  }, [refreshLists])

  if (loading) {
    return (
      <Screen>
        <Text style={styles.muted}>Loading…</Text>
      </Screen>
    )
  }

  const leagues = groups.filter(group => group.kind === 'league')
  // recentGame asks for played rows, so pending revisions here retain an
  // approved score/recording. A projected unapproved event has neither.
  const recentPublished =
    recent != null &&
    (!recent.pendingApproval ||
      recent.videoCount > 0 ||
      recent.homeScore != null ||
      recent.awayScore != null)

  // Followed groups sit in the same sections, after the person's own, and
  // never twice.
  const followedOf = (kind: GroupKind, own: Set<string>): PublicGroup[] =>
    followed.filter(group => group.kind === kind && !own.has(group.id))
  const followedTeams = followedOf('team', new Set(teams.map(team => team.id)))
  const followedSeasons = followedOf(
    'season',
    new Set(seasons.map(season => season.id))
  )
  const followedLeagues = followedOf(
    'league',
    new Set(leagues.map(league => league.id))
  )
  const counts = {
    league: leagues.length + followedLeagues.length,
    season: seasons.length + followedSeasons.length,
    team: teams.length + followedTeams.length
  }
  const missingKinds = (['league', 'season', 'team'] as const).filter(
    kind => counts[kind] === 0
  )
  const canCreate = props.onCreateGroup != null && error == null
  const titleOf = (kind: GroupKind): string =>
    `${kind[0].toUpperCase()}${kind.slice(1)}`
  /**
   * `Create Team` beside a section's title. Finding a group to follow is the
   * Search tab's job, so Home only creates.
   */
  const sectionActions = (
    kind: GroupKind
  ): Array<{
    label: string
    accessibilityLabel: string
    onPress: () => void
  }> =>
    canCreate
      ? [
          {
            label: `Create ${titleOf(kind)}`,
            accessibilityLabel: `Create ${titleOf(kind)}`,
            onPress: () => props.onCreateGroup?.(kind)
          }
        ]
      : []
  const followedRow = (group: PublicGroup): React.ReactElement => (
    <ListRow
      title={group.name}
      teamName={group.kind === 'team' ? group.name : undefined}
      subtitle={
        group.kind === 'season'
          ? seasonPeriodLabel(group.startsOn, group.endsOn)
          : (group.parentNames[0] ??
            (group.locality == null
              ? undefined
              : localityLabel(group.locality)))
      }
      emblem={initialsOf(group.name)}
      emblemUri={group.imageUrl}
      badgeLabel="Following"
      badgeTone="neutral"
      onPress={() => {
        props.onOpenGroup?.({ kind: group.kind, id: group.id })
      }}
    />
  )
  // Nothing at all: the cards lead. Otherwise each kind still missing has
  // its card below everything the person already has.
  const showEmptyHero = canCreate && missingKinds.length === 3
  const descriptions: Record<GroupKind, string> = {
    league:
      'Create an organization that runs multiple seasons or tournaments. Leagues represent a longstanding organization that runs repeated seasons with new or recurring teams.',
    season:
      'Create a season or tournament with a game schedule for selected teams. A season is a time-scoped duration of games such as Spring 2027, Fall 2026, or 2026 Labor Day Classic. Seasons can be standalone or managed by a league.',
    team: 'A team is a collection of coaches, staff, and players that can stand alone, be added to a season, and be managed by staff of the team, its parent seasons, or its parent league.'
  }
  const creationCards = (kinds: readonly GroupKind[]): React.ReactElement => (
    <View
      style={{
        flexDirection: 'row',
        flexWrap: 'wrap',
        gap: 16,
        marginBottom: 20
      }}
    >
      {kinds.map(kind => (
        <CreationCard
          key={kind}
          title={`Create ${kind[0].toUpperCase()}${kind.slice(1)}`}
          icon={creationIcons[kind]}
          description={descriptions[kind]}
          onPress={() => props.onCreateGroup?.(kind)}
        />
      ))}
    </View>
  )
  return (
    <Screen>
      {error != null && <Text style={styles.error}>{error}</Text>}
      {recentError != null && <Text style={styles.error}>{recentError}</Text>}
      {showEmptyHero && creationCards(missingKinds)}
      {live.length > 0 && !offline && (
        <View style={styles.section}>
          <SectionHeader title="Live now" />
          <Card style={styles.gameCard}>
            <View style={styles.livePoster}>
              <LivePill />
              <VideoPoster
                size="hero"
                videoUrl={shown ? liveUrl : undefined}
                // From where the game is now, as every live viewer plays it.
                live
                onVideoGone={() => {
                  // The player has given up on that address. Forgetting which
                  // game it belonged to is all it takes: the next look asks
                  // for a fresh one, which is what a server restarted mid-game
                  // leaves everybody needing.
                  playing.current = undefined
                  setLiveUrl(undefined)
                }}
              />
            </View>
            <Text style={styles.gameMeta}>
              {live[0].awayTeamName} at {live[0].homeTeamName}
              {live[0].startedAt == null
                ? ''
                : ` · started ${startedAtLabel(live[0].startedAt)}`}
              {live[0].angles > 1 ? ` · ${live[0].angles} cameras` : ''}
            </Text>
            <Button
              label="Watch live"
              icon="play_arrow"
              onPress={() => {
                onOpenGame(live[0].gameId, liveTitle(live[0]), {
                  autoPlay: true
                })
              }}
            />
          </Card>

          {/*
            Two games at once is rare and it does happen — a family with a
            child on each of two teams, on a Saturday. The first plays in the
            card; the rest are rows, because two streams playing at once on one
            screen is nobody's idea of a home page.
          */}
          {live.slice(1).map(game => (
            <ListCard key={game.videoId}>
              <ListRow
                title={liveTitle(game)}
                subtitle={[
                  game.startedAt == null
                    ? undefined
                    : `started ${startedAtLabel(game.startedAt)}`,
                  game.angles > 1 ? `${game.angles} cameras` : undefined
                ]
                  .filter(part => part != null)
                  .join(' · ')}
                teamName={game.homeTeamName}
                badgeLabel="live"
                badgeTone="live"
                onPress={() => {
                  onOpenGame(game.gameId, liveTitle(game), { autoPlay: true })
                }}
              />
            </ListCard>
          ))}
        </View>
      )}

      {recent != null && (
        <View style={styles.section}>
          <SectionHeader title="Recent game" />
          <Card style={styles.gameCard}>
            {/*
              Over a picture the card has no line of its own for the date, so
              it sits above; drawn plainly, the card carries it, with the
              status on the same line at the right.
            */}
            {recent.posterUrl != null && recent.posterUrl !== '' && (
              <Text style={styles.gameMeta}>
                {shortDate(recent.startAt)}
                {recent.venue == null ? '' : ` · ${recent.venue}`}
              </Text>
            )}
            <GameHeroCard
              awayName={recent.awayTeamName}
              homeName={recent.homeTeamName}
              awayScore={recent.awayScore}
              homeScore={recent.homeScore}
              status={gameStatusLine(recent)}
              pendingApproval={recent.pendingApproval}
              live={recent.isGameLive === true}
              date={shortDate(recent.startAt)}
              venue={recent.venue ?? undefined}
              winner={winnerOf(recent)}

              awayLogoUrl={recent.awayLogoUrl}
              homeLogoUrl={recent.homeLogoUrl}
              posterUrl={recent.posterUrl}
              videoUrl={offline || !shown ? undefined : recentUrl}
              videoLive={recentLive && !offline}
            />
            {/*
              Two buttons, not one. There was no way to reach a box score from
              this screen at all: a game that was scored and never filmed had
              nothing to press.
            */}
            <View style={styles.actions}>
              {recent.videoCount > 0 && offline && recent.isLive && (
                <Text style={styles.muted}>{OFFLINE_UNPLAYABLE}</Text>
              )}
              {recent.videoCount > 0 && !(offline && recent.isLive) && (
                <Button
                  label="Watch"
                  icon="play_arrow"
                  style={styles.action}
                  onPress={() => {
                    onOpenGame(recent.id, matchup(recent), { autoPlay: true })
                  }}
                />
              )}
              {recentPublished && (
                <Button
                  label="Box Score"
                  icon="format_list_bulleted"
                  kind="secondary"
                  style={styles.action}
                  onPress={() => {
                    onOpenGame(recent.id, matchup(recent), { tab: 'boxScore' })
                  }}
                />
              )}
              {recent.pendingApproval && recent.pendingRequestId != null && (
                <Button
                  label="Open game proposal"
                  kind="secondary"
                  style={styles.action}
                  onPress={() => {
                    onOpenGame(recent.id, matchup(recent), {
                      pendingRequestId: recent.pendingRequestId
                    })
                  }}
                />
              )}
            </View>
          </Card>
        </View>
      )}

      {players.length > 0 && (
        <View style={styles.section}>
          <SectionHeader title="Your Players" />
          <ListCard>
            {players.map(player => {
              const name = player.shortName ?? String(player.userId)
              return (
                <ListRow
                  key={player.userId}
                  title={name}
                  emblem={initialsOf(name)}
                  emblemUri={player.avatarUrl}
                  onPress={() => {
                    props.onOpenPlayer?.(player.userId, name)
                  }}
                />
              )
            })}
          </ListCard>
        </View>
      )}

      {props.onOpenUmpireSchedule != null && (
        <View style={styles.section}>
          <SectionHeader title="Help Umpire" />
          <ListCard>
            <ListRow
              title="Umpire Schedule"
              leading={
                <UmpireMaskDrawing
                  size={theme.rem(1.6)}
                  color={theme.primaryTextColor}
                />
              }
              onPress={props.onOpenUmpireSchedule}
            />
          </ListCard>
        </View>
      )}

      {counts.team > 0 && (
        <View style={styles.section}>
          <SectionHeader title="Your teams" actions={sectionActions('team')} />
          <ListCard>
            {teams.map(team => (
              <ListRow
                key={team.id}
                title={team.name}
                subtitle={subtitleFor(team)}
                teamName={team.name}
                emblemUri={team.logoUrl}
                onPress={() => {
                  onOpenTeam(team)
                }}
              />
            ))}
            {followedTeams.map(group => (
              <View key={group.id}>{followedRow(group)}</View>
            ))}
          </ListCard>
        </View>
      )}

      {counts.season > 0 && (
        <View style={styles.section}>
          <SectionHeader title="Seasons" actions={sectionActions('season')} />
          <ListCard>
            {seasons.map(season => (
              <ListRow
                key={season.id}
                title={season.name}
                subtitle={`${season.teams} teams · ${season.games} games · ${season.videos} videos`}
                emblemIcon="event"
                emblemUri={season.logoUrl}
                badgeLabel={season.term ?? undefined}
                badgeTone="neutral"
                onPress={() => {
                  props.onOpenGroup == null
                    ? onOpenSeason(season)
                    : props.onOpenGroup({ kind: 'season', id: season.id })
                }}
              />
            ))}
            {followedSeasons.map(group => (
              <View key={group.id}>{followedRow(group)}</View>
            ))}
          </ListCard>
        </View>
      )}
      {counts.league > 0 && (
        <View style={styles.section}>
          <SectionHeader
            title="Your leagues"
            actions={sectionActions('league')}
          />
          <ListCard>
            {leagues.map(league => (
              <ListRow
                key={league.id}
                title={league.name}
                emblem={initialsOf(league.name)}
                emblemUri={league.imageUrl}
                onPress={() => {
                  props.onOpenGroup?.({ kind: 'league', id: league.id })
                }}
              />
            ))}
            {followedLeagues.map(group => (
              <View key={group.id}>{followedRow(group)}</View>
            ))}
          </ListCard>
        </View>
      )}
      {canCreate &&
        !showEmptyHero &&
        missingKinds.length > 0 &&
        creationCards(missingKinds)}
    </Screen>
  )
}

/** `Alta Hawks @ Bay Stars`, for the screen a live game opens on. */
function liveTitle(game: LiveNow): string {
  return `${game.awayTeamName} @ ${game.homeTeamName}`
}

/** `3:18 pm`, in the reader's own clock. */
function startedAtLabel(startedAt: string): string {
  return new Date(startedAt)
    .toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' })
    .toLowerCase()
}

/** `Alta Hawks @ Bay Stars`, the way the row in a list reads. */
function matchup(game: SeasonGame): string {
  const lines = matchupLines(game)
  return `${lines.first} ${lines.second}`
}

/** `QPLL Spring 2026 · 12–3`, or whichever half of that we know. */
function subtitleFor(team: TeamSummary): string | undefined {
  const parts: string[] = []
  if (team.record?.seasonName != null) parts.push(team.record.seasonName)
  else if (team.shortName != null) parts.push(team.shortName)
  if (team.record != null) {
    const { w, l, t } = team.record
    // An en dash: it is a record, not a range of numbers, and the hyphen is
    // visibly too short beside the numerals at this weight.
    parts.push(t > 0 ? `${w}–${l}–${t}` : `${w}–${l}`)
  }
  return parts.length === 0 ? undefined : parts.join(' · ')
}

const getStyles = cacheStyles((theme: Theme) => ({
  section: {
    gap: theme.rem(theme.spaceMdRem)
  },
  gameCard: {
    gap: theme.rem(theme.spaceMdRem)
  },
  livePoster: {
    position: 'relative'
  },
  actions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: theme.rem(theme.spaceSmRem)
  },
  action: {
    flexGrow: 1,
    flexBasis: theme.rem(10)
  },
  gameMeta: {
    fontFamily: theme.semiBoldTextFont,
    fontSize: theme.rem(0.75),
    fontWeight: '700',
    letterSpacing: theme.rem(0.04),
    color: theme.secondaryTextColor
  },
  muted: {
    fontFamily: theme.regularTextFont,
    fontSize: theme.rem(0.9375),
    color: theme.secondaryTextColor
  },
  emptyTitle: {
    fontFamily: theme.semiBoldTextFont,
    fontSize: theme.rem(1.125),
    fontWeight: '600',
    color: theme.primaryTextColor,
    marginBottom: theme.rem(theme.spaceSmRem)
  },
  error: {
    fontFamily: theme.regularTextFont,
    fontSize: theme.rem(0.875),
    color: theme.dangerColor
  }
}))
